/**
 * Persistance IndexedDB de l'application : deux object stores, `media` pour
 * les dépôts photo/vidéo des invités et `voice` pour le livre d'or vocal.
 */
import type { StoredMedia, VoiceNote } from "@/types/album";

const DATABASE_NAME = "sq-album";
const DATABASE_VERSION = 1;
const MEDIA_STORE = "media";
const VOICE_STORE = "voice";
const CREATED_AT_INDEX = "createdAt";

/** Le stockage local est indisponible ou inutilisable (navigation privée, IndexedDB désactivé…). */
export class StorageUnavailableError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "StorageUnavailableError";
  }
}

/** Distingue un dépassement de quota des autres échecs de stockage. */
export function isQuotaError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "QuotaExceededError";
}

function toStorageError(error: DOMException | null): Error {
  if (error && isQuotaError(error)) return error;
  return new StorageUnavailableError("L'opération de stockage local a échoué.", { cause: error });
}

let dbPromise: Promise<IDBDatabase> | null = null;

function createDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new StorageUnavailableError("IndexedDB n'est pas disponible dans cet environnement."));
      return;
    }

    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    } catch (error) {
      reject(
        new StorageUnavailableError("Impossible d'ouvrir la base de stockage locale.", {
          cause: error,
        }),
      );
      return;
    }

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(MEDIA_STORE)) {
        db.createObjectStore(MEDIA_STORE, { keyPath: "id" }).createIndex(
          CREATED_AT_INDEX,
          CREATED_AT_INDEX,
        );
      }
      if (!db.objectStoreNames.contains(VOICE_STORE)) {
        db.createObjectStore(VOICE_STORE, { keyPath: "id" }).createIndex(
          CREATED_AT_INDEX,
          CREATED_AT_INDEX,
        );
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => {
        db.close();
        // La connexion mémoïsée devient inutilisable : la relâcher permet à la
        // prochaine opération d'en rouvrir une, au lieu d'échouer jusqu'au
        // rechargement de la page.
        dbPromise = null;
      };
      resolve(db);
    };

    request.onerror = () => {
      dbPromise = null;
      reject(toStorageError(request.error));
    };

    request.onblocked = () => {
      dbPromise = null;
      reject(
        new StorageUnavailableError(
          "L'ouverture du stockage local est bloquée par un autre onglet.",
        ),
      );
    };
  });
}

/** Ouvre (ou réutilise) la connexion à la base IndexedDB de l'application. */
export function openDatabase(): Promise<IDBDatabase> {
  dbPromise ??= createDatabase();
  return dbPromise;
}

/**
 * Exécute une opération sur un store au sein d'une transaction et ne résout
 * qu'à `transaction.oncomplete` : c'est le seul moment où l'écriture est garantie durable.
 */
function withTransaction<T>(
  db: IDBDatabase,
  storeName: string,
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    let transaction: IDBTransaction;
    let store: IDBObjectStore;
    let request: IDBRequest<T>;

    try {
      // L'ouverture de transaction elle-même peut lever : la connexion a pu
      // être fermée entre-temps par `onversionchange` (autre onglet, effacement
      // des données du site). Sans ce garde, l'erreur remonterait brute, hors
      // du type d'erreur que le reste du module garantit.
      transaction = db.transaction(storeName, mode);
      store = transaction.objectStore(storeName);
    } catch (error) {
      dbPromise = null;
      reject(
        error instanceof DOMException
          ? toStorageError(error)
          : new StorageUnavailableError("Le stockage local n'est plus disponible.", {
              cause: error,
            }),
      );
      return;
    }

    try {
      request = action(store);
    } catch (error) {
      reject(
        error instanceof DOMException
          ? toStorageError(error)
          : new StorageUnavailableError("Impossible d'exécuter l'opération de stockage local.", {
              cause: error,
            }),
      );
      return;
    }

    transaction.oncomplete = () => resolve(request.result);
    transaction.onerror = () => reject(toStorageError(transaction.error));
    transaction.onabort = () => reject(toStorageError(transaction.error));
  });
}

export async function putMedia(record: StoredMedia): Promise<void> {
  const db = await openDatabase();
  await withTransaction(db, MEDIA_STORE, "readwrite", (store) => store.put(record));
}

export async function getAllMedia(): Promise<StoredMedia[]> {
  const db = await openDatabase();
  return withTransaction<StoredMedia[]>(db, MEDIA_STORE, "readonly", (store) => store.getAll());
}

export async function deleteMedia(id: string): Promise<void> {
  const db = await openDatabase();
  await withTransaction(db, MEDIA_STORE, "readwrite", (store) => store.delete(id));
}

export async function putVoice(record: VoiceNote): Promise<void> {
  const db = await openDatabase();
  await withTransaction(db, VOICE_STORE, "readwrite", (store) => store.put(record));
}

export async function getAllVoice(): Promise<VoiceNote[]> {
  const db = await openDatabase();
  return withTransaction<VoiceNote[]>(db, VOICE_STORE, "readonly", (store) => store.getAll());
}

export async function deleteVoice(id: string): Promise<void> {
  const db = await openDatabase();
  await withTransaction(db, VOICE_STORE, "readwrite", (store) => store.delete(id));
}

/** Indique si le stockage local est utilisable dans cet environnement. */
export async function isStorageAvailable(): Promise<boolean> {
  try {
    await openDatabase();
    return true;
  } catch {
    return false;
  }
}

/** Estime l'espace de stockage utilisé et disponible, quand l'API le permet. */
export async function estimateUsage(): Promise<{ usage: number; quota: number } | null> {
  if (typeof navigator === "undefined" || !navigator.storage?.estimate) {
    return null;
  }

  try {
    const { usage, quota } = await navigator.storage.estimate();
    if (usage === undefined || quota === undefined) return null;
    return { usage, quota };
  } catch {
    return null;
  }
}
